import type { ReactNode, RefObject } from 'react';
import type { TFunction } from 'i18next';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { CloseMatchChip, FieldNotice, SimilarNameNudge } from './SimilarNameNudge';
import { ComboCaret } from './StockEntryCombo';
import styles from './StockEntryDialog.module.css';
import type { ITrackedMaterial } from './StockEntryDialog.types';

interface IStockEntryMaterialFieldProps {
  comboRef: RefObject<HTMLDivElement | null>;
  open: boolean;
  /** Combined change handler — mirrors the original inline `onChange`, which
   *  both updates the name AND force-opens the list (not the click path's
   *  conditional `tryOpen`). */
  onInputChange: (value: string) => void;
  onInputClick: () => void;
  onCaretClick: () => void;
  onBlurAway: () => void;
  value: string;
  className: string;
  /** True when the dialog targets ONE existing tracked material (Sync, or a row
   *  re-count), so the material is fixed — a faded, non-editable value, never
   *  the combobox. The new-material flows leave it editable. */
  locked?: boolean;
  options: string[];
  /** Names that are close matches (separators aside, the same name) —
   *  they wear the chip. A plain substring hit is not in the set. */
  closeMatches: Set<string>;
  /** The first close match — the under-field nudge names it (the delivery
   *  form's exact treatment; one look for both forms). */
  closeMatch: string | null;
  /** One-record-per-material (design 2026-08-31): the tracked record the typed
   *  name fold-matches. Non-null blocks the save (the form disables it) and
   *  replaces the nudge with the hard-rule notice. */
  trackedHit: ITrackedMaterial | null;
  onOpenExisting?: (name: string) => void;
  query: string;
  exactMatch: boolean;
  onPick: (name: string, isNew?: boolean) => void;
  /** What sits beside the material in its row — the amount and unit, laid
   *  out like the delivery dialog's material line. */
  children?: ReactNode;
  t: TFunction;
}

/** The material combobox — filters delivery-name suggestions as the user
 *  types, and offers an add-new row for anything unmatched. Opens on click or
 *  typing, never on focus alone (so the dialog's autofocus doesn't pop it). */
export function StockEntryMaterialField({
  comboRef,
  open,
  onInputChange,
  onInputClick,
  onCaretClick,
  onBlurAway,
  value,
  className,
  locked,
  options,
  closeMatches,
  closeMatch,
  trackedHit,
  onOpenExisting,
  query,
  exactMatch,
  onPick,
  children,
  t,
}: IStockEntryMaterialFieldProps) {
  // The material is fixed when this dialog reconciles or re-counts one
  // existing row's stock: a faded locked value — no input, no caret, nothing
  // to change. Same treatment as the Arrived and unit fields.
  const material = locked ? (
    <span className={`${styles.input} ${styles.lockedField}`}>{value}</span>
  ) : (
    <div
      ref={comboRef}
      className={styles.combo}
      onBlur={(e) => {
        // Keyboard path (Tab away); pointer paths are handled by the
        // hook's document pointerdown listener.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onBlurAway();
      }}
    >
      {/* Opens on CLICK or typing — never on focus alone, so the
          dialog's autofocus doesn't pop the list on open. */}
      <input
        id="stock-material"
        className={className}
        value={value}
        onChange={(e) => onInputChange(e.target.value)}
        onClick={onInputClick}
        maxLength={120}
        placeholder={t('logistics.orderDialog.itemExample')}
        autoComplete="off"
      />
      <ComboCaret onClick={onCaretClick} />
      {open && (options.length > 0 || (query && !exactMatch)) ? (
        <div
          className={styles.comboList}
          role="listbox"
          onMouseDown={(e) => e.preventDefault()}
        >
          <ScrollArea>
            {/* Add-new leads the list so a typed name is always one click away. */}
            {query && !exactMatch ? (
              <button type="button" className={styles.comboNew} onClick={() => onPick(query, true)}>
                {t('logistics.stockDialog.addNew', { name: query })}
              </button>
            ) : null}
            {options.length > 0 ? (
              <>
                <div className={styles.comboGroup}>{t('logistics.stockDialog.groupDeliveries')}</div>
                {options.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={closeMatches.has(s) ? `${styles.comboOpt} ${styles.comboOptStrong}` : styles.comboOpt}
                    onClick={() => onPick(s)}
                  >
                    {s}
                    <CloseMatchChip show={closeMatches.has(s)} t={t} />
                  </button>
                ))}
              </>
            ) : null}
          </ScrollArea>
        </div>
      ) : null}
    </div>
  );
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={locked ? undefined : 'stock-material'}>
        {t('logistics.stockDialog.material')}
      </label>
      {/* One row, like the delivery dialog's material line: the material,
          then the amount and unit beside it. */}
      <div className={styles.materialRow} data-material-row>
        <div className={styles.materialCell}>{material}</div>
        {children}
      </div>
      {locked ? null : (
        <>
          <SimilarNameNudge
            closeMatch={trackedHit != null ? null : closeMatch}
            visible={!open}
            message={t('logistics.similarName.nudgeStock', { name: closeMatch ?? '' })}
            onUse={(name) => onPick(name)}
            t={t}
          />
          <FieldNotice
            tone="error"
            visible={trackedHit != null && !open}
            message={t('logistics.oneOpen.stock', { ...(trackedHit ?? { name: '', amount: 0, unit: '' }) })}
            actionLabel={t('logistics.oneOpen.open')}
            onAction={() => trackedHit != null && onOpenExisting?.(trackedHit.name)}
          />
        </>
      )}
    </div>
  );
}
